import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Phone, Mail, MapPin, Globe, User, Send, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PHONE, PHONE_LINK, WHATSAPP_LINK, EMAIL, ADDRESS, MAP_EMBED } from "../data/content";
import { Reveal, SectionHeading } from "../lib/motion";
import { t } from "../i18n";

const EMPTY = { name: "", phone: "", email: "", message: "" };

export const ContactInfo = () => (
  <div className="space-y-5">
    <div className="glass-card rounded-xl p-6" data-testid="contact-person-card">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 border border-gold/40">
          <User className="h-5 w-5 text-gold" />
        </span>
        <div>
          <p className="font-display font-bold text-white">Mr. Dheeraj Kataria</p>
          <p className="text-xs tracking-[0.2em] text-gold font-display mt-0.5">THE TRIP WORLD</p>
        </div>
      </div>
    </div>
    {[
      { icon: Phone, label: t("contact.phone"), value: PHONE, href: PHONE_LINK, testid: "contact-phone" },
      { icon: Mail, label: t("contact.emailLabel"), value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email" },
      { icon: Globe, label: t("contact.website"), value: "thetrip.world", href: "https://thetrip.world", testid: "contact-website" },
      { icon: MapPin, label: t("contact.location"), value: ADDRESS, testid: "contact-address" },
    ].map((row) => (
      <a
        key={row.label}
        href={row.href || undefined}
        target={row.href?.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        data-testid={row.testid}
        className={`glass-card rounded-xl p-5 flex items-center gap-4 ${row.href ? "card-lift" : ""}`}
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gold/10 border border-gold/30">
          <row.icon className="h-5 w-5 text-gold" />
        </span>
        <span>
          <span className="block text-[11px] font-bold tracking-[0.2em] text-slate-500 font-display uppercase">{row.label}</span>
          <span className="block text-sm text-slate-200 mt-0.5">{row.value}</span>
        </span>
      </a>
    ))}
    <div className="flex gap-3">
      <a href={PHONE_LINK} data-testid="contact-call-btn" className="flex-1 btn-gold rounded-full py-3.5 text-xs font-bold tracking-wider font-display flex items-center justify-center gap-2">
        <Phone className="h-4 w-4" /> {t("contact.call")}
      </a>
      <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="flex-1 bg-[#25D366] text-navy-deep rounded-full py-3.5 text-xs font-bold tracking-wider font-display flex items-center justify-center gap-2">
        <MessageCircle className="h-4 w-4" /> {t("contact.whatsapp")}
      </a>
      <a href={`mailto:${EMAIL}`} data-testid="contact-email-btn" className="flex-1 btn-outline-light rounded-full py-3.5 text-xs font-bold tracking-wider font-display flex items-center justify-center gap-2">
        <Mail className="h-4 w-4" /> {t("contact.emailBtn")}
      </a>
    </div>
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, source: "contact" });
      setDone(true);
      setForm(EMPTY);
      toast.success("Message sent. We will get back to you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="glass-card rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
        <CheckCircle2 className="h-14 w-14 text-gold mb-5" />
        <h3 className="font-display text-2xl font-bold text-white mb-3">{t("contact.successTitle")}</h3>
        <p className="text-slate-400 text-sm max-w-sm leading-relaxed">
          {t("contact.successText")}
        </p>
        <button onClick={() => setDone(false)} data-testid="contact-another-btn" className="mt-8 btn-outline-light rounded-full px-6 py-3 text-xs font-bold tracking-wider font-display">
          {t("contact.another")}
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="glass-card rounded-2xl p-6 sm:p-8 space-y-5" data-testid="contact-form">
      <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t("contact.formName")} data-testid="contact-name-input" className="input-premium" />
      <input required minLength={6} value={form.phone} onChange={set("phone")} placeholder={t("contact.formPhone")} data-testid="contact-phone-input" className="input-premium" />
      <input required type="email" value={form.email} onChange={set("email")} placeholder={t("contact.formEmail")} data-testid="contact-email-input" className="input-premium" />
      <textarea required value={form.message} onChange={set("message")} placeholder={t("contact.formMessage")} rows={5} data-testid="contact-message-input" className="input-premium resize-none" />
      <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-gold w-full rounded-full py-4 text-sm font-bold tracking-wider font-display flex items-center justify-center gap-2 disabled:opacity-60">
        {loading ? t("contact.sending") : t("contact.submit")} <Send className="h-4 w-4" />
      </button>
    </form>
  );
};

export const MapEmbed = () => (
  <Reveal className="mt-16">
    <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl" data-testid="google-map-embed">
      <iframe
        title="The Trip World — Ahmadgarh, Punjab"
        src={MAP_EMBED}
        className="w-full h-[380px] grayscale-[30%] contrast-[1.05]"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  </Reveal>
);

const ContactSection = () => (
  <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-navy-deep overflow-hidden">
    <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-gold/5 blur-3xl" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
        description={t("contact.desc")}
        testid="contact-heading"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Reveal><ContactInfo /></Reveal>
        <Reveal delay={0.15}><ContactForm /></Reveal>
      </div>
      <MapEmbed />
    </div>
  </section>
);

export default ContactSection;
